import { IAssetUrlDTO, IEventRequestParams } from './shared.dto';

export type EventParams = Omit<IEventRequestParams, 'timeZone'>;

export abstract class ICustomPagesDAO {
    public abstract getCustomPageTypes(params: EventParams): Promise<ICustomPageTypesDTO>;
    public abstract getCustomPagesConfiguration(params: EventParams): Promise<ICustomPageConfigurationDTO>;
    public abstract updateCustomPagesConfiguration(params: EventParams, payload: ICustomPageConfigurationDTO): Promise<ICustomPageConfigurationDTO>;
    public abstract getS3AssetUrl(params: IEventRequestParams): Promise<IAssetUrlDTO>;
    public abstract uploadFileToS3(file: File, params: IEventRequestParams): Promise<IAssetUrlDTO>;
    public abstract getDefaultPageConfigByType(type: DefaultPageType, params: EventParams): Promise<DefaultPageConfigurationType>;
    public abstract updateDefaultPageConfig(payload: DefaultPageConfigurationType, params: EventParams): Promise<DefaultPageConfigurationType>;
    public abstract getSessionsPageLayoutOptions(params: EventParams & { layout: PageLayoutType }): Promise<ISessionsPageLayoutOptionsDTO>;
    public abstract getUpcomingSessionsTypeOptions(params: EventParams): Promise<IUpcomingSessionsTypeOptionsDTO>;
    public abstract getReplaysPageLayoutOptions(params: EventParams & { layout: PageLayoutType }): Promise<IReplaysPageLayoutOptionsDTO>;
    public abstract getMeetingPresenterGroups(params: EventParams): Promise<IMeetingPresenterGroupDTO[]>;
}

export enum PageLayoutType {
    SESSION_LAYOUT = 'SESSION_LAYOUT',
    REPLAYS_LAYOUT = 'REPLAYS_LAYOUT',
}

export enum SessionPageLayoutOptions {
    GridViewByTrack = 'Grid view - by Track',
    CarouselViewByTrack = 'Carousel View - by Track',
    CarouselViewByDay = 'Carousel View - by Day'
}

export enum DefaultCarouselView {
    ShowAll = 'SHOW_ALL',
    Collapsed = 'COLLAPSED',
}

export enum UpcomingSessionsTypeOptions {
    NextSessionOnly = 'Next Session Only',
    AllUpcomingSessions = 'All Upcoming Sessions',
}

export enum ReplaysPageLayoutOptions {
    GridViewByMeetingDate = 'Grid View - By Meeting Date',
    GridViewByTrack = 'Grid view - by Track',
}

export enum CustomPageType {
    Knovio = 'Knovio',
    EmbeddedWebpage = 'Embedded webpage',
    RichText = 'Rich Text',
    EmbeddedHTML = 'Embedded HTML',
    UserShowcase = 'User Showcase',
    ExternalLink= 'External Link',
    SingleMeeting = 'Single Meeting',
    BookAMeeting = 'Book a Meeting'
}

export const NotLandingPageTypes = [
    CustomPageType.ExternalLink
];

export interface ICustomPageTypesDTO {
    types: CustomPageType[];
}

export interface ISessionsPageLayoutOptionsDTO {
    types: SessionPageLayoutOptions[];
}

export interface IUpcomingSessionsTypeOptionsDTO {
    types: UpcomingSessionsTypeOptions[];
}

export interface IReplaysPageLayoutOptionsDTO {
    types: ReplaysPageLayoutOptions[];
}

export interface IMeetingPresenterGroupDTO {
    meetingId: number;
    presenterNames: string[];
}

export interface ICustomPageConfigurationDTO {
    pages: CustomPageConfigurationType[];
}

export interface IKnovioCustomPageConfiguration extends ICustomPageConfig {
    embedCodeId: string;
    type: CustomPageType.Knovio;
}

export interface IEmbeddedWebpageCustomPageConfiguration extends ICustomPageConfig {
    type: CustomPageType.EmbeddedWebpage;
    embeddedWebpageUrl: string;
}

export interface IRichTextCustomPageConfiguration extends ICustomPageConfig {
    type: CustomPageType.RichText;
    richText: string;
}

export interface IShowcaseCustomPageConfiguration extends ICustomPageConfig {
    type: CustomPageType.UserShowcase;
    embedCodeId: string;
}

export interface IExternalLinkCustomPageConfiguration extends ICustomPageConfig {
    type: CustomPageType.ExternalLink;
    externalLink: string;
}

export interface ISingleMeetingCustomPageConfiguration extends ICustomPageConfig {
    type: CustomPageType.SingleMeeting;
    meetingId: number;
}

export interface IBookAMeetingCustomPageConfiguration extends ICustomPageConfig {
    type: CustomPageType.BookAMeeting;
    presentersFilterEnabled: boolean;
    selectedMeetingIds: string[];
    presenterGroups?: IMeetingPresenterGroupDTO[];
}

export interface IEmbeddedHTMLCustomPageConfiguration extends ICustomPageConfig {
    type: CustomPageType.EmbeddedHTML;
    html?: IEmbeddedHTMLCustomPageFileConfiguration;
}

export interface IEmbeddedHTMLCustomPageFileConfiguration {
    objectKey: string;
    title: string;
}

export type CustomPageConfigurationType =
    | IKnovioCustomPageConfiguration
    | IEmbeddedWebpageCustomPageConfiguration
    | IRichTextCustomPageConfiguration
    | IEmbeddedHTMLCustomPageConfiguration
    | IShowcaseCustomPageConfiguration
    | IExternalLinkCustomPageConfiguration
    | ISingleMeetingCustomPageConfiguration
    | IBookAMeetingCustomPageConfiguration
;

export interface IPassportPageDTO {
    type: CustomPageType | DefaultPageType;
    label: string;
    order?: number;
    enabled: boolean;
    publicEnabled?: boolean;
    landingPage: boolean;
    pageLinkEnabled?: boolean;
    linkDescription?: string;
}

export interface ICustomPageConfig extends IPassportPageDTO {
    customPageId?: number;
    path: string;
    type: CustomPageType;
    restrictAccessEnabled: boolean;
    restrictAccessFilters: RestrictAccessFilter[];
}

export interface IRestrictFilterProps {
    organizationTypes: string[];
    customFieldsOptionsList: CustomFieldsOptionsListDTO[];
}

/* DEFAULT PAGES */
export enum DefaultPageType {
    Welcome = 'Welcome',
    Agenda = 'Agenda',
    Session = 'Session',
    Showcase = 'Showcase',
    Support = 'Support',
    Speakers = 'Speakers',
    Replays = 'Replays'
}
export const GENERAL_SESSIONS = 'General Sessions';
export const MY_SCHEDULE = 'My Schedule';
export const REPLAYS = 'Replays';

export enum SortOrganizationsByOptions {
    ALPHABETICAL_ORDER = 'ALPHABETICAL_ORDER',
    PRIORITY_IN_EVENT = 'PRIORITY_IN_EVENT',
}

/* DEFAULT PUBLIC PAGES */
export const DefaultPublicPages: (CustomPageType | DefaultPageType)[] = [
    DefaultPageType.Speakers,
    DefaultPageType.Session
];

interface IDefaultPageConfiguration extends IPassportPageDTO {
    type: DefaultPageType;
}

export interface WelcomePageConfiguration extends IDefaultPageConfiguration {
    type: DefaultPageType.Welcome;
    welcomeKnovioEnabled: boolean;
    welcomeKnovioEmbedId: string;
    calendarButtonEnabled: boolean;
}
export interface AgendaPageConfiguration extends IDefaultPageConfiguration {
    type: DefaultPageType.Agenda;
    topicEnabled: boolean;
    scheduleNotes: string;
    restrictAccessEnabled: boolean;
    restrictAccessFilters: RestrictAccessFilter[];
}

export type CustomFieldsOptionsListDTO = {
    customFieldId: string;
    label: string;
    optionsList: string[];
};
export type GetCustomFieldsOptionsList = {
    fields: CustomFieldsOptionsListDTO[];
    triagedRegistrationEnabled: boolean;
};
export type AllowedOptions = Record<string, string[]>;
export interface RestrictAccessFilter {
    allowedOrganizationTypes?: string[];
    allowedAttendanceMethods?: string[];
    allowedOptions?: AllowedOptions;
}
export interface SessionPageConfiguration extends IDefaultPageConfiguration {
    type: DefaultPageType.Session;
    layout: SessionPageLayoutOptions;
    defaultCarouselView: DefaultCarouselView;
    restrictAccessEnabled: boolean;
    restrictAccessFilters: RestrictAccessFilter[];
    preRegistrationEnabled: boolean;
    hostAttendeesEnabled: boolean;
    sessionNotes: string;
    pastSessionsEnabled: boolean;
    registerButtonLabel: string;
    subscribeAttendeeToMeetingOnRegistrationEnabled?: boolean;
}
export interface ShowcasePageConfiguration extends IDefaultPageConfiguration {
    type: DefaultPageType.Showcase;
    embedCodeId: string;
}
export interface SupportPageConfiguration extends IDefaultPageConfiguration {
    type: DefaultPageType.Support;
    supportInformation: string;
}
export interface SpeakersPageConfiguration extends IDefaultPageConfiguration {
    type: DefaultPageType.Speakers;
    sortOrganizationsBy: SortOrganizationsByOptions;
}
export interface ReplaysPageConfiguration extends IDefaultPageConfiguration {
    type: DefaultPageType.Replays;
    upcomingSessionType: UpcomingSessionsTypeOptions;
    upcomingSessionsEnabled: boolean;
    hostAttendeesEnabled: boolean;
    upcomingSessionsLabel: string;
    replaysSessionLabel: string;
    replaysPreRegistrationEnabled: boolean;
    replaysLayout: ReplaysPageLayoutOptions;
    replaysNotes: string;
    restrictAccessEnabled: boolean;
    restrictAccessFilters: RestrictAccessFilter[];
    replaysDefaultCarouselView?: DefaultCarouselView;
}

export type DefaultPageConfigurationType =
    | WelcomePageConfiguration
    | AgendaPageConfiguration
    | SessionPageConfiguration
    | ShowcasePageConfiguration
    | SupportPageConfiguration
    | SpeakersPageConfiguration
    | ReplaysPageConfiguration
;

export interface ISessionsModalProps {
    sessionsLayoutOptions: SessionPageLayoutOptions[];
    organizationTypes: string[];
    customFieldsOptionsList: CustomFieldsOptionsListDTO[];
}

export interface IReplaysModalProps {
    upcomingSessionsTypeOptions: UpcomingSessionsTypeOptions[];
    replaysLayoutOptions: ReplaysPageLayoutOptions[];
    organizationTypes: string[];
    customFieldsOptionsList: CustomFieldsOptionsListDTO[];
}

export interface IAgendaModalProps {
    organizationTypes: string[];
    customFieldsOptionsList: CustomFieldsOptionsListDTO[];
}

export enum AttendanceType {
    InPerson = 'IN_PERSON',
    Virtual = 'VIRTUAL',
    Both = 'BOTH'
}